import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { INK, MUTED, BODY, LINE_SOFT } from './leaveTheme';
// Grouping + paging shared by the Leaves list screens (Leave summary, Approvals,
// Configuration allocations). Groups nest in the order the user ticked them.
export const PAGE_SIZE = 80;
export function buildGroups(items, keys, keyOf, depth = 0, parentId = '') {
    if (!keys.length)
        return [];
    const [head, ...rest] = keys;
    const m = new Map();
    for (const x of items) {
        const k = keyOf(head, x);
        if (!m.has(k.key))
            m.set(k.key, { k, items: [] });
        m.get(k.key).items.push(x);
    }
    return [...m.values()]
        .sort((a, b) => (a.k.sort ?? a.k.label).localeCompare(b.k.sort ?? b.k.label))
        .map(g => {
        const id = `${parentId}/${head}:${g.k.key}`;
        return { id, key: g.k.key, label: g.k.label, depth, items: g.items, children: buildGroups(g.items, rest, keyOf, depth + 1, id) };
    });
}
/** Client-side paging, 80 per page. Returns the current slice and the pager props. Resets to page 1 when `resetKey` changes. */
export function usePaging(items, resetKey) {
    const [page, setPage] = useState(0);
    useEffect(() => { setPage(0); }, [resetKey]);
    const last = Math.max(0, Math.ceil(items.length / PAGE_SIZE) - 1);
    const p = Math.min(page, last);
    const slice = useMemo(() => items.slice(p * PAGE_SIZE, (p + 1) * PAGE_SIZE), [items, p]);
    return { slice, pager: { page: p, pageSize: PAGE_SIZE, total: items.length, onPage: setPage } };
}
/** A list under collapsible group headers ("Earned Leave (9)", total at the right). Without group keys it is a plain list. */
export function GroupedList({ items, groupKeys, keyOf, renderRow, headerRight, headerIcon }) {
    const nodes = useMemo(() => buildGroups(items, groupKeys, keyOf), [items, groupKeys, keyOf]);
    const [closed, setClosed] = useState(new Set());
    const toggle = (id) => setClosed(prev => { const n = new Set(prev); if (n.has(id))
        n.delete(id);
    else
        n.add(id); return n; });
    const renderNode = (g) => {
        const open = !closed.has(g.id);
        return (_jsxs(Box, { children: [_jsxs(Box, { role: "button", tabIndex: 0, "aria-expanded": open, onClick: () => toggle(g.id), onKeyDown: e => { if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggle(g.id);
                    } }, sx: { display: 'flex', alignItems: 'center', gap: 1, pl: 1.5 + g.depth * 2, pr: 1.5, minHeight: 30, cursor: 'pointer', bgcolor: g.depth ? '#FAFBF6' : '#F6F8EF', borderTop: `0.5px solid ${LINE_SOFT}`, '&:hover': { bgcolor: '#EEF2E2' } }, children: [_jsx(Box, { component: "span", "aria-hidden": true, sx: { fontSize: 9, color: MUTED, width: 12, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }, children: "\u25B6" }), headerIcon?.(g), _jsxs(Typography, { sx: { fontSize: 12.5, fontWeight: 600, color: INK, flex: 1 }, children: [g.label, " (", g.items.length, ")"] }), _jsx(Typography, { component: "div", sx: { fontSize: 12, fontWeight: 600, color: BODY }, children: headerRight?.(g) })] }), open && (g.children.length ? g.children.map(renderNode) : g.items.map(x => renderRow(x, g.depth + 1)))] }, g.id));
    };
    if (!groupKeys.length)
        return _jsx(_Fragment, { children: items.map(x => renderRow(x, 0)) });
    return _jsx(_Fragment, { children: nodes.map(renderNode) });
}
